import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { get } from 'lodash';
import { ChevronDown, ChevronUp, Paperclip, Trash2, X } from 'lucide-react';

import { OrderSchemaType } from '../order.schema';
import { DETAILS_FORM_DATA } from '../constants';
import { useAttachmentHandler } from '../Hooks/useAttachmentHandler';

import {
  Title,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Textarea,
  Select,
  CollapsibleHeader,
  CircularCollapseIcon,
  Separator,
  AttachmentList,
  AttachmentListItem,
  RemoveAttachmentButton,
  AttachmentInputContainer,
  AttachmentTrigger,
  AttachmentPlaceholder,
  TrashIconContainer,
  SwitchBox,
  SwitchInput,
  SwitchSlider
} from '../style';

const DetailsSection: React.FC = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);

  const { control, register, formState: { errors } } = useFormContext<OrderSchemaType>();

  const {
    attachments,
    attachmentError,
    fileInputRef,
    handleFileSelectClick,
    handleFileChange,
    handleRemoveAttachment,
    handleRemoveAllAttachments,
  } = useAttachmentHandler({ fieldPath: 'attachments' });

  return (
    <>
      <CollapsibleHeader onClick={() => setIsDetailsOpen(prev => !prev)}>
        <Title>Detalhes do pedido</Title>
        <CircularCollapseIcon onClick={(e) => { e.stopPropagation(); setIsDetailsOpen(prev => !prev); }}>
          {isDetailsOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </CircularCollapseIcon>
      </CollapsibleHeader>

      {isDetailsOpen && (
        <>
          <FormGrid>
            <FormGroup>
              <Label htmlFor="orderType">Tipo de Pedido</Label>
              <Controller
                name="orderType"
                control={control}
                render={({ field }) => (
                  <Select id="orderType" {...field} value={field.value ?? ''} hasError={!!get(errors, 'orderType')}>
                    <option value="">Selecione</option>
                    {DETAILS_FORM_DATA.orderTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </Select>
                )}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="orderDate">Data do Pedido</Label>
              <Input id="orderDate" type="date" {...register('orderDate')} hasError={!!get(errors, 'orderDate')} />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="deliveryDate">Prazo de Entrega</Label>
              <Input id="deliveryDate" type="date" {...register('deliveryDate')} hasError={!!get(errors, 'deliveryDate')} />
            </FormGroup>
          </FormGrid>

          <FormGrid>
            <FormGroup>
              <Label htmlFor="attachments">Anexos</Label>
              <AttachmentInputContainer>
                <AttachmentTrigger onClick={handleFileSelectClick} hasError={!!attachmentError}>
                  <Paperclip size={16} />
                  <AttachmentPlaceholder>Clique para anexar arquivo</AttachmentPlaceholder>
                </AttachmentTrigger>
                {attachments.length > 0 && (
                  <TrashIconContainer
                    as="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveAllAttachments();
                    }}
                  >
                    <Trash2 size={16} />
                  </TrashIconContainer>
                )}
              </AttachmentInputContainer>
              {attachments.length > 0 && (
                <AttachmentList>
                  {attachments.map((file, index) => (
                    <AttachmentListItem key={index}>
                      {file.name}
                      <RemoveAttachmentButton
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemoveAttachment(index);
                        }}
                      >
                        <X size={12} />
                      </RemoveAttachmentButton>
                    </AttachmentListItem>
                  ))}
                </AttachmentList>
              )}
              <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept=".pdf,.xml,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.png"
              />
              {attachmentError && <p style={{ color: 'red', fontSize: '12px' }}>{attachmentError}</p>}
            </FormGroup>

            <FormGroup>
              <Label>Informação extra</Label>
              <Input {...register('extraInfo')} placeholder="Digite" />
            </FormGroup>
          </FormGrid>

          <FormGrid columns={3}>
            <FormGroup style={{ alignItems: 'flex-start' }}>
              <Label htmlFor="criticalAnalysis">Análise Crítica</Label>
              <Controller
                name="criticalAnalysis"
                control={control}
                render={({ field }) => (
                  <SwitchBox>
                    <SwitchInput
                      type="checkbox"
                      id="criticalAnalysis"
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                    <SwitchSlider className="slider"></SwitchSlider>
                  </SwitchBox>
                )}
              />
            </FormGroup>

            <FormGroup style={{ gridColumn: 'span 2' }}>
              <Label>Descrição da Análise</Label>
              <Textarea 
                {...register('criticalAnalysisDesc')} 
                placeholder="Digite a descrição da análise" 
              />
            </FormGroup>
          </FormGrid>
        </>
      )}
      <Separator />
    </>
  );
};

export default DetailsSection;